import { useState } from "react";
import { clipStreamUrl, clipThumbUrl, clipDownloadUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Play, Download, Flame, Captions, Droplet, ScanFace, Pencil } from "lucide-react";
import { motion } from "framer-motion";
import { ClipEditor } from "@/components/ClipEditor";

function scoreColor(s) {
  if (s >= 85) return "text-yellow-400 border-yellow-400/40 bg-yellow-400/10";
  if (s >= 70) return "text-cyan-400 border-cyan-400/40 bg-cyan-400/10";
  return "text-amber-400 border-amber-400/40 bg-amber-400/10";
}

const RUBRIC = { hook: 20, curiosity: 15, value: 15, emotion: 10, standalone: 10, pacing: 10, shareability: 10, payoff: 10 };
const EARLIER_RUBRIC = { hook: 30, value: 25, payoff: 25, standalone: 20 };

function ScoreBreakdown({ clip }) {
  const currentRubric = ["editor-v4", "editor-v5-quality", "editor-v5-balanced", "editor-v5-quantity"].includes(clip.selection_version);
  const limits = currentRubric ? RUBRIC : EARLIER_RUBRIC;
  return (
    <div data-testid={`clip-score-breakdown-${clip.id}`} className="my-3 space-y-3">
      <p data-testid={`clip-rubric-label-${clip.id}`} className="text-[10px] uppercase tracking-wider text-copy-500">{currentRubric ? "8-part quality assessment" : "Earlier 4-part assessment · not rescored"}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">
        {Object.entries(limits).map(([key, max]) => {
          const score = clip.quality_scores[key];
          const weak = score < max / 2;
          return <div key={key} data-testid={`clip-score-${key}-${clip.id}`}>
            <div className="flex justify-between gap-1 text-[11px] capitalize text-copy-300"><span>{key}</span><b className={weak ? "text-amber-400" : "text-cyan-300"}>{score}/{max}</b></div>
            <div aria-hidden="true" className="mt-1 h-1 rounded-full bg-surface-800 overflow-hidden"><div className={`h-full rounded-full ${weak ? "bg-amber-400" : "bg-cyan-400"}`} style={{ width: `${score / max * 100}%` }} /></div>
          </div>;
        })}
      </div>
      {currentRubric && <>
        <p data-testid={`clip-score-calculation-${clip.id}`} className="border-t border-surface-800 pt-3 text-xs text-copy-300">{clip.quality_subtotal}/100 subtotal − {clip.penalty_total} penalties = <b className={clip.virality_score < 70 ? "text-amber-400" : "text-cyan-300"}>{clip.virality_score}/100</b></p>
        {clip.quality_subtotal < clip.penalty_total && <p data-testid={`clip-score-floor-${clip.id}`} className="text-[10px] text-copy-500">Final scores have a minimum of zero.</p>}
        <div data-testid={`clip-penalties-${clip.id}`} className="space-y-2">
          {clip.quality_penalties?.length ? clip.quality_penalties.map((penalty, i) => <p key={i} data-testid={`clip-penalty-${clip.id}-${i}`} className="text-xs leading-relaxed text-amber-300"><b>−{penalty.points}</b> {penalty.reason}</p>) : <p data-testid={`clip-no-penalties-${clip.id}`} className="text-xs text-copy-500">No penalties applied</p>}
        </div>
        {clip.quantity_note && <p data-testid={`clip-quantity-note-${clip.id}`} className="text-xs text-amber-300">{clip.quantity_note}</p>}
      </>}
    </div>
  );
}

export function ClipCard({ clip: initialClip, index = 0, editable = false, onEdited }) {
  const [clip, setClip] = useState(initialClip);
  const [playing, setPlaying] = useState(false);
  const [editing, setEditing] = useState(false);
  const [bust, setBust] = useState(0);

  const handleEdited = (updated) => {
    setClip(updated);
    setPlaying(false);
    setBust(Date.now());
    onEdited && onEdited(updated);
  };

  const isManual = clip.selection_version === 'manual-range';
  const canEdit = editable && !isManual;
  const aspectClass = isManual ? '' : { "1:1": "aspect-square", "16:9": "aspect-video" }[clip.aspect_ratio] || "aspect-[9/16]";

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.06, 0.5), duration: 0.4 }}
      className="group rounded-2xl border border-surface-800 bg-panel overflow-hidden hover:-translate-y-1 hover:border-surface-700 transition-[transform,border-color] duration-300"
      data-testid={`clip-card-${clip.id}`}
    >
      <div className={`media-surface relative ${aspectClass} bg-black overflow-hidden`} style={isManual ? { aspectRatio: `${clip.width} / ${clip.height}` } : undefined}>
        {playing ? (
          <video
            data-testid={`clip-video-${clip.id}`}
            src={`${clipStreamUrl(clip.id)}?v=${bust}`}
            controls
            autoPlay
            playsInline
            className="w-full h-full object-cover"
          />
        ) : (
          <>
            <img src={`${clipThumbUrl(clip.id)}?v=${bust}`} alt={clip.title}
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.opacity = 0; }} />
            <button
              data-testid={`clip-play-${clip.id}`}
              onClick={() => setPlaying(true)}
              className="absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/70 via-black/10 to-black/30"
            >
              <span className="h-14 w-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="h-6 w-6 text-black ml-0.5" fill="currentColor" />
              </span>
            </button>
            <div data-testid={`clip-score-${clip.id}`} title={isManual ? 'Manually selected · no AI score' : 'AI editorial assessment, not a prediction of views'} className={`absolute top-3 left-3 px-2.5 py-1 rounded-full border text-xs font-bold flex items-center gap-1 ${isManual ? 'text-cyan-400 border-cyan-400/40 bg-cyan-400/10' : scoreColor(clip.virality_score)} glass`}>
              {isManual ? 'Manual selection' : <><Flame className="h-3 w-3" /> {clip.selection_version === "manual-trim" ? "Edited" : typeof clip.virality_score === "number" ? `${clip.virality_score} / 100` : "Style sample"}</>}
            </div>
            {canEdit && (
              <button data-testid={`clip-edit-${clip.id}`} onClick={() => setEditing(true)}
                className="absolute top-3 right-3 h-8 w-8 rounded-full glass border border-white/10 flex items-center justify-center text-white hover:bg-white/20 transition-colors">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            <div className="absolute bottom-3 left-3 right-3 flex flex-wrap gap-1.5">
              {clip.captioned && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium glass border border-white/10 text-cyan-300 flex items-center gap-1">
                  <Captions className="h-3 w-3" /> Captions
                </span>
              )}
              {clip.face_tracked && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium glass border border-white/10 text-fuchsia-300 flex items-center gap-1">
                  <ScanFace className="h-3 w-3" /> Face
                </span>
              )}
              {clip.watermarked && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium glass border border-white/10 text-copy-300 flex items-center gap-1">
                  <Droplet className="h-3 w-3" /> Watermark
                </span>
              )}
            </div>
          </>
        )}
      </div>

      <div className="p-4">
        {clip.template_name && <p data-testid={`clip-style-${clip.id}`} className="text-[10px] uppercase tracking-widest text-cyan-400 mb-2">{clip.template_name}</p>}
        <h3 data-testid={`clip-title-${clip.id}`} className="font-display font-semibold leading-tight line-clamp-2">{clip.title}</h3>
        {clip.hook && <p data-testid={`clip-hook-${clip.id}`} className="text-xs text-copy-400 mt-1.5 line-clamp-2">“{clip.hook}”</p>}
        {clip.framing_summary && <p data-testid={`clip-framing-${clip.id}`} className={`text-xs leading-relaxed mt-2 ${clip.framing_summary.needs_review ? 'text-amber-300' : 'text-copy-400'}`}>
          {clip.framing_summary.needs_review ? `Review framing · ${Math.round(clip.framing_summary.source_seconds)}s preserves the source where a confident crop was unavailable` : `Speaker framing · ${clip.framing_summary.scene_count} camera shots checked`}
          {clip.framing_summary.duet_seconds > 0 && <span className="block text-copy-500">Uncertain conversations keep both people in large panels</span>}
        </p>}
        {(clip.reason || clip.quality_scores) && (
          <details className="mt-3 border-t border-surface-800 pt-3" data-testid={`clip-rationale-${clip.id}`}>
            <summary data-testid={`clip-rationale-toggle-${clip.id}`} className="cursor-pointer text-xs text-copy-300 hover:text-cyan-300">Why this moment?</summary>
            <p data-testid={`clip-reason-${clip.id}`} className="mt-2 text-xs leading-relaxed text-copy-400">{clip.reason}</p>
            {clip.quality_scores && <ScoreBreakdown clip={clip} />}
            {clip.payoff && <p data-testid={`clip-payoff-${clip.id}`} className="mt-2 text-xs text-copy-400"><span className="text-copy-200">Closing words:</span> “{clip.payoff}”</p>}
            {clip.transcript_excerpt && <p data-testid={`clip-transcript-${clip.id}`} className="mt-3 max-h-40 overflow-y-auto text-xs leading-relaxed text-copy-400">{clip.transcript_excerpt}</p>}
            <p data-testid={`clip-score-disclaimer-${clip.id}`} className="mt-2 text-[10px] text-copy-500">AI editorial assessment · not a prediction of views</p>
          </details>
        )}
        <div className="flex items-center justify-between mt-3">
          <span className="text-xs text-copy-600">{Math.round(clip.duration)}s clip</span>
          <div className="flex items-center gap-2">
            {canEdit && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}
                data-testid={`clip-edit-btn-${clip.id}`}
                className="rounded-full border-surface-700 bg-transparent hover:bg-surface-800 h-8 px-3 text-xs">
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
              </Button>
            )}
            <a href={clipDownloadUrl(clip.id)} download data-testid={`clip-download-${clip.id}`}>
              <Button size="sm" className="rounded-full bg-surface-100 text-black hover:bg-white h-8 px-3 text-xs font-semibold">
                <Download className="h-3.5 w-3.5 mr-1.5" /> Download
              </Button>
            </a>
          </div>
        </div>
      </div>
      {canEdit && <ClipEditor clip={clip} open={editing} onOpenChange={setEditing} onEdited={handleEdited} />}
    </motion.div>
  );
}
